import { v4 as uuid } from 'uuid';
import { memo, useCallback, useState} from "react";
import Dashboard_V2_1H2 from "../../assets/icons/dashboard/layout_icon_v2_1h2";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import { selectChartConfig, selectChartDrawings } from "../../selectors/chart";
import { getDefaultLayout, getDefaultWidget } from "../widgetDashboard/widgetDashboard.helper";
import { WidgetTypes } from '../widgetDashboard/widgetDashboard.enums';
import { selectPrimarySecurity } from '../../selectors/home';
import { getSecurityKey } from '../../util/security';
import NameDialog from '../common/nameDialog';
import { saveUserDashboard } from '../../services/apis/dashboard';
import { useDispatch } from 'react-redux';
import { updateUserSavedLayouts } from '../../reducer/widgetDashboard.reducer';
import AnalyticsManager from '../../analytics/AnalyticsManager';
import { updateUserDashboard } from '../../reducer/user.reducer';
import { generatePath, useNavigate } from 'react-router-dom';
import ROUTES from '../../assets/routes';
import { updatePanesConfig, updateSavedChartConfigs } from '../../reducer/chart.reducer';
import { DashboardShareStatus, Platform } from '../../proto/generated/user-preference'
import { selectIsMobile } from '../../selectors/device'
export type LoadAsLayoutProps = {
    chartView: string,
    disabled?: boolean
}
const LoadAsLayoutButton: React.FC<LoadAsLayoutProps> = ({ chartView, disabled }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const chartConfig = useSelector(selectChartConfig(chartView));
    const chartDrawings = useSelector(selectChartDrawings(chartView));
    const { expressionMode, expressionSecurities, key } = useSelector(selectPrimarySecurity);
    const [showNameDialog, setShowNameDialog] = useState(false);
    const isMobile = useSelector(selectIsMobile);
    const onClick = useCallback(() => {
        setShowNameDialog(true);
    }, [chartConfig, chartDrawings]);

    const onSaveLayoutConfirm = useCallback(async (name: string, openInNewTab: boolean) => {
        const newLayoutId = uuid().replace(/[-]*/g, '').split('').slice(0, 10).join('')
        const dashboard = getDefaultLayout();
        const securitiesList = expressionMode ? expressionSecurities.map(v => getSecurityKey(v.exchange, v.code)) : [key];
        const widget = getDefaultWidget(newLayoutId, 1, WidgetTypes.CHART, key, expressionMode, securitiesList, chartConfig.timeBucket)
        dashboard.widgets[widget.slotId] = widget;
        const chartViewKey = widget.settings.chartId;

        const layoutToBeSaved = {
            panes: dashboard.panes,
            widgets: {
                [widget.slotId]: widget
            },
            syncSettings: dashboard.syncSettings,
            charts: {
                [chartViewKey]: chartConfig
            },
            drawings: {
                [chartViewKey]: chartDrawings || []
            }
        }

        const result = await saveUserDashboard({
            dashboard: {
                id: newLayoutId,
                layout: JSON.stringify(layoutToBeSaved),
                name: name || 'Unnamed',
                isDefault: false,
                isPredefined: false,
                shareState: DashboardShareStatus.NOT_SHARED,
                platform: isMobile ? Platform.MOBILE : Platform.DESKTOP
            }
        });
        dispatch(updateSavedChartConfigs({
            charts: {
                [chartViewKey]: chartConfig
            },
            drawings: {
                [chartViewKey]: chartDrawings || []
            }
        }))

        dispatch(updatePanesConfig({
            viewKey: chartViewKey,
            panesConfig: Object.values(chartConfig.panesConfig),
            series: Object.values(chartConfig.series),
            seriesGroups: Object.values(chartConfig.seriesGroups)
        }))
        dispatch(updateUserSavedLayouts({
            replace: false,
            layouts: {
                [newLayoutId]: dashboard
            }
        }))
        AnalyticsManager.logEvent('Load as Layout', { source: 'stockbar', name })
        setShowNameDialog(false);
        dispatch(updateUserDashboard(result));
        const url = generatePath(ROUTES.chart, { layoutId: result.id });
        if (openInNewTab) {
            window.open(url, "_blank")
        } else {
            navigate(url)
        }
    }, [chartConfig, chartDrawings, key, expressionMode, expressionSecurities, chartView]);
    return <>
            <Button variant='text' sx={{ p: 0, textWrap: 'nowrap', minWidth: 'max-content' }} disabled={disabled} disableRipple onClick={onClick}>Save As Layout</Button>
            {showNameDialog && <NameDialog
                open
                onClose={() => {
                    setShowNameDialog(false)
                }}
                onSubmit={onSaveLayoutConfirm}
                title="Save Layout"
                subTitle="Name"
                currentName={''}
                showCheckbox
                defaultOpenInNewTab={false}
            />}
    </>
}
export default memo(LoadAsLayoutButton)